import { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  makeStyles,
  createStyles,
  Menu,
  MenuItem,
  IconButton,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import SmileIcon from '@material-ui/icons/SentimentSatisfiedAlt';
import { Draggable, Droppable } from 'react-beautiful-dnd';

import TextInput from 'src/components/TextInput';
import { CampaignProps } from 'src/types/campaign';
import {
  CAMPAIGN_DND_TYPE,
  FolderNode,
  folderDraggableId,
  isValidFolderName,
} from 'src/utils/campaignFolderPath';

import { CampaignItem } from './CampaignItem';

export const useStyles = makeStyles(() =>
  createStyles({
    collapseTitle: {
      fontSize: 14,
      fontWeight: 400,
    },
    campaignsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      margin: '8px 0',
      padding: 8,
      paddingLeft: 20,
      paddingRight: 8,
      position: 'relative',
      width: '100%',

      '&::before': {
        content: '""',
        position: 'absolute',
        left: 8,
        width: 1,
        top: 4,
        bottom: 4,
        borderLeft: '1px solid gray',
      },
    },
    nameContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      flex: 1,
      minWidth: 0,
    },
    dragHandle: {
      display: 'flex',
      alignItems: 'center',
      cursor: 'grab',
      flexShrink: 0,
    },
    titleInput: {
      '& .MuiInputBase-input': {
        fontSize: 14,
        fontWeight: 400,
        paddingTop: 0,
      },
    },
    errorText: {
      fontSize: 11,
      color: '#ff0000',
      marginLeft: 28,
      marginTop: 2,
    },
    combineTarget: {
      backgroundColor: 'rgba(94, 161, 242, 0.18)',
      borderRadius: 4,
    },
    campaignDropTarget: {
      width: '100%',
      borderRadius: 4,
    },
    campaignDropTargetActive: {
      backgroundColor: 'rgba(94, 161, 242, 0.12)',
    },
  })
);

interface CampaignFolderProps {
  node: FolderNode;
  index: number;
  depth: number;
  isOpen: boolean;
  showActions?: boolean;
  onToggleOpen: (path: string) => void;
  onSelectCampaign: (campaignId: string) => void;
  onDeleteFolder: (path: string) => void;
  onRenameFolder: (path: string, newName: string) => void;
  onAddSubfolder: (parentPath: string) => void;
  onEditCampaign?: (campaign: CampaignProps) => void;
  onDeleteCampaign?: (campaign: CampaignProps) => void;
  matchesSearch: (campaign: CampaignProps) => boolean;
}

export const CampaignFolder = ({
  node,
  index,
  depth,
  isOpen,
  showActions = false,
  onToggleOpen,
  onDeleteFolder,
  onRenameFolder,
  onAddSubfolder,
  onSelectCampaign,
  onEditCampaign,
  onDeleteCampaign,
  matchesSearch,
}: CampaignFolderProps) => {
  const classes = useStyles();
  const [folderName, setFolderName] = useState<string>(node.name);
  const [renameError, setRenameError] = useState<string>('');
  const [isRenaming, setIsRenaming] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const visibleCampaigns = node.campaigns.filter(matchesSearch);
  const itemCount = visibleCampaigns.length + node.children.length;
  const hasExpandableContent =
    node.children.length > 0 || visibleCampaigns.length > 0;

  useEffect(() => {
    setFolderName(node.name);
    setRenameError('');
  }, [node.name]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleRename = () => {
    setIsRenaming(true);
    setRenameError('');
    handleCloseMenu();
  };

  const handleFinishEditing = () => {
    const trimmed = folderName.trim();
    if (trimmed === node.name) {
      setIsRenaming(false);
      setRenameError('');
      return;
    }
    if (!isValidFolderName(trimmed)) {
      setRenameError('Name cannot be empty or contain "_sub_"');
      return;
    }
    setIsRenaming(false);
    setRenameError('');
    onRenameFolder(node.path, trimmed);
  };

  return (
    <Draggable
      draggableId={folderDraggableId(node.path)}
      index={index}
      isDragDisabled={isRenaming}
    >
      {(dragProvided, dragSnapshot) => (
        <Grid
          container
          ref={dragProvided.innerRef}
          {...dragProvided.draggableProps}
          className={
            dragSnapshot.combineTargetFor ? classes.combineTarget : undefined
          }
          style={{
            ...dragProvided.draggableProps.style,
            paddingLeft: depth * 16,
          }}
        >
          {/*
            Campaign Droppable wraps the folder name so hovering a campaign
            over the name drops into this folder (same as before nesting).
          */}
          <Droppable droppableId={node.path} type={CAMPAIGN_DND_TYPE}>
            {(provided, snapshot) => (
              <Grid
                container
                direction='column'
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`${classes.campaignDropTarget} ${
                  snapshot.isDraggingOver
                    ? classes.campaignDropTargetActive
                    : ''
                }`}
              >
                <Grid
                  container
                  item
                  direction='row'
                  justifyContent='space-between'
                  alignItems='center'
                  style={{ cursor: 'pointer' }}
                >
                  <div className={classes.nameContainer}>
                    <div
                      className={classes.dragHandle}
                      {...dragProvided.dragHandleProps}
                      title='Drag onto another folder to nest'
                    >
                      <SmileIcon htmlColor='#757575' style={{ fontSize: 18 }} />
                    </div>
                    <div
                      style={{ flex: 1, minWidth: 0 }}
                      onClick={() => {
                        if (!isRenaming && hasExpandableContent) {
                          onToggleOpen(node.path);
                        }
                      }}
                    >
                      {isRenaming ? (
                        <TextInput
                          className={classes.titleInput}
                          value={folderName}
                          onChange={(value) => {
                            setFolderName(value);
                            setRenameError('');
                          }}
                          onEnter={handleFinishEditing}
                          onBlur={handleFinishEditing}
                        />
                      ) : (
                        <Typography className={classes.collapseTitle}>
                          {node.name} ({itemCount})
                        </Typography>
                      )}
                    </div>
                  </div>
                  {hasExpandableContent ? (
                    isOpen ? (
                      <KeyboardArrowUpIcon
                        onClick={() => onToggleOpen(node.path)}
                        style={{ fontSize: 18 }}
                      />
                    ) : (
                      <KeyboardArrowDownIcon
                        onClick={() => onToggleOpen(node.path)}
                        style={{ fontSize: 18 }}
                      />
                    )
                  ) : (
                    <span style={{ width: 18 }} />
                  )}
                  <div>
                    <IconButton
                      onClick={handleOpenMenu}
                      style={{ padding: 2, marginLeft: 10 }}
                    >
                      <MoreVertIcon
                        htmlColor='#757575'
                        style={{ fontSize: 16 }}
                      />
                    </IconButton>

                    <Menu
                      id={`campaign-folder-menu-${node.path}`}
                      aria-labelledby='campaign-folder-button'
                      anchorEl={anchorEl}
                      open={openMenu}
                      onClose={handleCloseMenu}
                      onClick={handleCloseMenu}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          onAddSubfolder(node.path);
                          handleCloseMenu();
                        }}
                      >
                        Add subfolder
                      </MenuItem>
                      <MenuItem onClick={handleRename}>Rename</MenuItem>
                      <MenuItem
                        onClick={() => {
                          onDeleteFolder(node.path);
                          handleCloseMenu();
                        }}
                        style={{ color: '#ff0000' }}
                      >
                        Delete
                      </MenuItem>
                    </Menu>
                  </div>
                </Grid>
                {renameError ? (
                  <Typography className={classes.errorText}>
                    {renameError}
                  </Typography>
                ) : null}
                {isOpen && visibleCampaigns.length > 0 ? (
                  <Grid container className={classes.campaignsContainer}>
                    {visibleCampaigns.map((campaign, campaignIndex) => (
                      <CampaignItem
                        key={campaign.id}
                        index={campaignIndex}
                        campaign={campaign}
                        showActions={showActions}
                        onSelectCampaign={onSelectCampaign}
                        onEditCampaign={onEditCampaign}
                        onDeleteCampaign={onDeleteCampaign}
                      />
                    ))}
                  </Grid>
                ) : null}
                {provided.placeholder}
              </Grid>
            )}
          </Droppable>
        </Grid>
      )}
    </Draggable>
  );
};
